import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthWrapper } from "@/components/AuthWrapper"; // Force rebuild
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import BusinessSummary from "./pages/BusinessSummary";
import History from "./pages/History";
import CapTable from "./pages/CapTable";
import Financials from "./pages/Financials";
import Legal from "./pages/Legal";
import Market from "./pages/Market";
import Product from "./pages/Product";
import Risks from "./pages/Risks";
import Team from "./pages/Team";
import Traction from "./pages/Traction";
import UseOfFunds from "./pages/UseOfFunds";
import CompanyFiles from "./pages/CompanyFiles";
import FinancialStatements from "./pages/FinancialStatements";
import ArchivedNewsletter from "./pages/ArchivedNewsletter";
import AdminDashboard from "./pages/AdminDashboard";
import { BusinessSummaryEditor } from "@/components/admin/editors/BusinessSummaryEditor";
import { TeamEditor } from "@/components/admin/editors/TeamEditor";
import { ProductEditor } from "@/components/admin/editors/ProductEditor";
import { MarketEditor } from "@/components/admin/editors/MarketEditor";
import { LegalEditor } from "@/components/admin/editors/LegalEditor";
import { RisksEditor } from "@/components/admin/editors/RisksEditor";
import { FundsEditor } from "@/components/admin/editors/FundsEditor";
import { FinancialsEditor } from "@/components/admin/editors/FinancialsEditor";
import { TractionEditor } from "@/components/admin/editors/TractionEditor";
import { CapTableEditor } from "@/components/admin/editors/CapTableEditor";
import { DownloadableFilesEditor } from "@/components/admin/editors/DownloadableFilesEditor";
import { OrganizationalStructureEditor } from "@/components/admin/editors/OrganizationalStructureEditor";
import { EmailTemplatesEditor } from "@/components/admin/editors/EmailTemplatesEditor";
import { ArchivedFilesEditor } from "@/components/admin/editors/ArchivedFilesEditor";

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-2">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Root redirect */}
        <Route 
          path="/" 
          element={
            <Suspense fallback={<PageLoader />}>
              <Navigate to="/business-summary" replace />
            </Suspense>
          } 
        />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AuthWrapper>
              <AdminDashboard />
            </AuthWrapper>
          }
        />
        
        {/* Admin Edit Routes */}
        <Route
          path="/admin/edit/business-summary"
          element={
            <AuthWrapper>
              <BusinessSummaryEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/team"
          element={
            <AuthWrapper>
              <TeamEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/product"
          element={
            <AuthWrapper>
              <ProductEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/market"
          element={
            <AuthWrapper>
              <MarketEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/legal"
          element={
            <AuthWrapper>
              <LegalEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/risks"
          element={
            <AuthWrapper>
              <RisksEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/funds"
          element={
            <AuthWrapper>
              <FundsEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/financials"
          element={
            <AuthWrapper>
              <FinancialsEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/traction"
          element={
            <AuthWrapper>
              <TractionEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/cap-table"
          element={
            <AuthWrapper>
              <CapTableEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/downloadable-files"
          element={
            <AuthWrapper>
              <DownloadableFilesEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/archived-files"
          element={
            <AuthWrapper>
              <ArchivedFilesEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/organizational-structure"
          element={
            <AuthWrapper>
              <OrganizationalStructureEditor />
            </AuthWrapper>
          }
        />
        <Route
          path="/admin/edit/email-templates"
          element={
            <AuthWrapper>
              <EmailTemplatesEditor />
            </AuthWrapper>
          }
        />
        
        {/* Main Routes */}
        <Route
          path="/business-summary"
          element={
            <AuthWrapper>
              <BusinessSummary />
            </AuthWrapper>
          }
        />
        <Route
          path="/history"
          element={
            <AuthWrapper>
              <History />
            </AuthWrapper>
          }
        />
        <Route
          path="/product"
          element={
            <AuthWrapper>
              <Product />
            </AuthWrapper>
          }
        />
        <Route
          path="/market"
          element={
            <AuthWrapper>
              <Market />
            </AuthWrapper>
          }
        />
        <Route
          path="/traction"
          element={
            <AuthWrapper>
              <Traction />
            </AuthWrapper>
          }
        />
        <Route
          path="/team"
          element={
            <AuthWrapper>
              <Team />
            </AuthWrapper>
          }
        />
        <Route
          path="/financials"
          element={
            <AuthWrapper>
              <Financials />
            </AuthWrapper>
          }
        />
        <Route
          path="/cap-table"
          element={
            <AuthWrapper>
              <CapTable />
            </AuthWrapper>
          }
        />
        <Route
          path="/use-of-funds"
          element={
            <AuthWrapper>
              <UseOfFunds />
            </AuthWrapper>
          }
        />
        <Route
          path="/risks"
          element={
            <AuthWrapper>
              <Risks />
            </AuthWrapper>
          }
        />
        <Route
          path="/legal"
          element={
            <AuthWrapper>
              <Legal />
            </AuthWrapper>
          }
        />
        <Route
          path="/company-files"
          element={
            <AuthWrapper>
              <CompanyFiles />
            </AuthWrapper>
          }
        />
        <Route
          path="/financial-statements"
          element={
            <AuthWrapper>
              <FinancialStatements />
            </AuthWrapper>
          }
        />
        <Route
          path="/archived-newsletter"
          element={
            <AuthWrapper>
              <ArchivedNewsletter />
            </AuthWrapper>
          }
        />
        
        {/* 404 Route - Must be last */}
        <Route
          path="*"
          element={
            <AuthWrapper>
              <NotFound />
            </AuthWrapper>
          }
        />
      </Routes>
      <Toaster />
    </Router>
  );
}

export default App;